import { Auth0ContextInterface, Auth0Provider } from '@auth0/auth0-react';
import React from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { hasAuth0Client, navAuth0Context } from '../ContextSwitcher/Context/NavAuthProvider';
import { Auth0Options } from '../types';

interface Auth0ContextWrapperProps {
  children: React.ReactNode;
  auth0Config?: Partial<Auth0Options>;
}

const warnNoClient = (action: string) =>
  console.info(`[Auth0ContextWrapper] No Auth0 client is available, so ${action} is unavailable.`);

/**
 * A signed-out session with every Auth0 action present, for when there is no real client. Every
 * member is implemented because nav-bar children call them: the utility tray's Log Out awaits
 * `logout`, for one.
 */
const fallbackAuth0Context: Auth0ContextInterface = {
  isAuthenticated: false,
  isLoading: false,
  error: undefined,
  user: undefined,
  // One function can't implement the overloads' `detailedResponse` variant; this never returns one.
  getAccessTokenSilently: (async () => {
    console.warn('[Auth0ContextWrapper] Auth0 access token unable to be retrieved.');
    return '';
  }) as Auth0ContextInterface['getAccessTokenSilently'],
  getAccessTokenWithPopup: async () => {
    warnNoClient('a popup token request');
    return undefined;
  },
  getIdTokenClaims: async () => undefined,
  loginWithRedirect: async () => warnNoClient('sign-in'),
  loginWithPopup: async () => warnNoClient('sign-in'),
  // Nothing to clear: there is no client session. The host's own sign-out runs before this.
  logout: async () => undefined,
  handleRedirectCallback: async () => {
    throw new Error('[Auth0ContextWrapper] No Auth0 client is available to handle a redirect callback.');
  }
};

/**
 * Supplies the placeholder context above instead of a real Auth0 client, for a host that
 * configured none and on an insecure origin. Children that call `useAuth0` get a signed-out
 * session, and a host that passes `auth0token` still works.
 */
const PlaceholderAuth0Provider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <navAuth0Context.Provider value={fallbackAuth0Context}>{children}</navAuth0Context.Provider>
);

/** Auth0 needs a secure (HTTPS) origin; on any other, the placeholder stands in for it. */
const InsecureAuth0Provider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  React.useEffect(() => {
    console.error(
      '[Auth0ContextWrapper] Running in insecure context (non-HTTPS) - Auth0Provider cannot be rendered. ' +
        'Note: Context switcher functionality will still work if an auth0token is provided via props. ' +
        'For full authentication features, please ensure you are running in an HTTPS environment.'
    );
  }, []);

  return <PlaceholderAuth0Provider>{children}</PlaceholderAuth0Provider>;
};

const SecureAuth0Provider: React.FC<Auth0ContextWrapperProps> = ({ children, auth0Config }) => {
  return (
    <Auth0Provider
      context={navAuth0Context}
      domain={auth0Config?.auth0domain ?? ''}
      clientId={auth0Config?.auth0clientID ?? ''}
      cacheLocation="localstorage"
      authorizationParams={{
        redirect_uri: auth0Config?.auth0callbackUrl,
        audience: auth0Config?.auth0audience
      }}
      onRedirectCallback={auth0Config?.onRedirectCallback}
      skipRedirectCallback={auth0Config?.auth0skipRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
};

const AUTH0_INSECURE_ERROR_MESSAGE = 'auth0-spa-js must run on a secure origin.';

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const MAX_BOUNDARY_RESETS = 5;

interface AuthErrorFallbackProps extends ErrorFallbackProps {
  resetCountRef: React.MutableRefObject<number>;
}

const AuthErrorFallback: React.FC<AuthErrorFallbackProps> = ({ resetErrorBoundary, resetCountRef }) => {
  React.useEffect(() => {
    if (resetCountRef.current < MAX_BOUNDARY_RESETS) {
      resetCountRef.current++;
      resetErrorBoundary();
    }
  }, [resetErrorBoundary, resetCountRef]);

  // Since there isn't a reasonable fallback UI for the entire nav bar when auth fails,
  // we render null to match the previous thrown error behavior
  return null;
};

export const Auth0ContextWrapper: React.FC<Auth0ContextWrapperProps> = (props) => {
  const [insecureContext, setInsecureContext] = React.useState<boolean>(false);

  /*
   * The retry budget has to outlive the fallback. It used to be a ref inside
   * `AuthErrorFallback`, which unmounts and remounts on every reset, so the count
   * restarted at zero each cycle and the cap never applied. Any error thrown during
   * render — not just an auth one — then looped catch/reset/rethrow indefinitely,
   * turning a single bad render into enough repeated work to lock up the tab.
   */
  const boundaryResetCount = React.useRef(0);

  const renderFallback = React.useCallback(
    (fallbackProps: ErrorFallbackProps) => <AuthErrorFallback {...fallbackProps} resetCountRef={boundaryResetCount} />,
    []
  );

  // Without a client, mounting `Auth0Provider` anyway makes it request
  // `https://authorize/?client_id=` on load and log a failed session refresh on every render.
  let provider: React.ReactNode;
  if (!hasAuth0Client(props.auth0Config)) {
    provider = <PlaceholderAuth0Provider>{props.children}</PlaceholderAuth0Provider>;
  } else if (insecureContext) {
    provider = <InsecureAuth0Provider {...props} />;
  } else {
    provider = <SecureAuth0Provider {...props} />;
  }

  return (
    <ErrorBoundary
      fallbackRender={renderFallback}
      onError={(error) => {
        if (error.message.includes(AUTH0_INSECURE_ERROR_MESSAGE)) {
          console.warn(
            '[Auth0ContextWrapper] Auth0 requires HTTPS - falling back to placeholder Auth0 provider without Auth0 integration'
          );
          setInsecureContext(true);
        }
        console.error('[Auth0ContextWrapper] Auth0 error:', error);
      }}
    >
      {provider}
    </ErrorBoundary>
  );
};
